import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Chip
} from '@mui/material';
import { Add as AddIcon, LocalHospital as DiseaseIcon } from '@mui/icons-material';
import { useEntitySearch } from '../../hooks/useEntitySearch';
import { useNotification } from '../../context/NotificationContext';
import {
  searchDiseases,
  getDisease,
  createDisease,
  updateDisease,
  deleteDisease
} from '../../api/diseases';
import { Disease, DiseaseCreate, DiseaseUpdate, DiseaseListItem } from '../../types/disease';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import EntitySearchResults from '../../components/entities/EntitySearchResults/EntitySearchResults';
import DiseaseForm from '../../components/entities/DiseaseForm/DiseaseForm';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const DiseasesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { results, loading: searchLoading, error: searchError, search, clearResults } = useEntitySearch<DiseaseListItem>(searchDiseases);

  const [selectedDisease, setSelectedDisease] = useState<Disease | null>(null);
  const [loadingDisease, setLoadingDisease] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = useCallback(async (query: string) => {
    await search(query);
  }, [search]);

  const handleSelectDisease = async (item: DiseaseListItem) => {
    setLoadingDisease(true);
    setError(null);

    try {
      const disease = await getDisease(item.id);
      setSelectedDisease(disease);
      clearResults(); // Clear search results after selection
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load disease');
      showNotification('Failed to load disease', 'error');
    } finally {
      setLoadingDisease(false);
    }
  };

  const handleNewDisease = () => {
    setSelectedDisease(null);
    setError(null);
  };

  const handleSave = async (data: DiseaseCreate | DiseaseUpdate, id?: string) => {
    try {
      if (id) {
        const updated = await updateDisease(id, data as DiseaseUpdate);
        setSelectedDisease(updated);
        showNotification('Disease updated successfully', 'success');
      } else {
        const created = await createDisease(data as DiseaseCreate);
        setSelectedDisease(created);
        showNotification('Disease created successfully', 'success');
      }
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDisease(id);
      setSelectedDisease(null);
      showNotification('Disease deleted successfully', 'success');
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const renderDiseaseItem = (disease: DiseaseListItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DiseaseIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {disease.name}
          </Typography>
        </Box>
        {disease.mesh_id && (
          <Chip label={disease.mesh_id} size="small" color="primary" variant="outlined" />
        )}
      </Box>
      {disease.summary_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {disease.summary_description.substring(0, 200)}
          {disease.summary_description.length > 200 ? '...' : ''}
        </Typography>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" className="py-6">
      {/* Top Toolbar */}
      <Box className="flex items-center justify-between gap-4 mb-6">
        <Typography variant="h4" component="h1" className="font-bold text-gray-800 flex items-center gap-2">
          <DiseaseIcon fontSize="large" className="text-blue-600" />
          Diseases
        </Typography>

        <Box className="flex items-center gap-3 flex-1 max-w-2xl">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Search diseases by name..."
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewDisease}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
          >
            New Disease
          </Button>
        </Box>
      </Box>

      {/* Search Loading Indicator */}
      {searchLoading && results.length === 0 && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <LoadingSpinner message="Searching diseases..." />
        </Paper>
      )}

      {/* Search Results - Show only if we have results */}
      {results.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Search Results
          </Typography>

          {searchError && <ErrorMessage message={searchError} />}

          {!searchError && (
            <EntitySearchResults
              results={results}
              onSelect={handleSelectDisease}
              renderItem={renderDiseaseItem}
            />
          )}
        </Paper>
      )}

      {/* Main Form Section */}
      <Paper elevation={2} className="p-6">
        <Typography variant="h5" className="mb-4 font-semibold text-gray-700 flex items-center gap-2">
          {selectedDisease ? (
            <>
              <DiseaseIcon className="text-blue-600" />
              Edit Disease: {selectedDisease.name}
            </>
          ) : (
            <>
              <AddIcon className="text-blue-600" />
              Create New Disease
            </>
          )}
        </Typography>

        {loadingDisease && <LoadingSpinner message="Loading disease..." />}

        {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

        {!loadingDisease && (
          <DiseaseForm
            disease={selectedDisease}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={handleNewDisease}
          />
        )}
      </Paper>
    </Container>
  );
};

export default DiseasesPage;
